import { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TreeFolder } from "@/domains/documents/types";
import type { BuildingProject } from "@/types/projects";
import {
  FolderIcon,
  ChevronRight,
  ChevronDown,
  FolderInput,
  Check,
  Copy,
  ArrowRight,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { folderService } from "@/domains/documents/services/folderService";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { ProjectSelectContent } from "@/domains/projects/status/ProjectSelectContent";
import { useProjectStatusFilter } from "@/domains/projects/status/useProjectStatusFilter";
import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger";

export type ProjectDestinationMode = "move" | "copy";

interface ProjectDestinationResult {
  projectId: string;
  folderId: string | null;
  mode: ProjectDestinationMode;
}

interface ProjectDestinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: ProjectDestinationResult) => void;
  projects: BuildingProject[];
  currentProjectId: string;
  buildingId: string;
  /** Number of items being moved/copied (for display) */
  itemCount?: number;
  /** Whether the operation is in progress */
  isLoading?: boolean;
}

/** The chain of folders from the tree root down to `folderId`, or null. */
function findFolderPath(folders: TreeFolder[], folderId: string): TreeFolder[] | null {
  for (const folder of folders) {
    if (folder.id === folderId) return [folder];
    const below = findFolderPath(folder.children ?? [], folderId);
    if (below) return [folder, ...below];
  }
  return null;
}

interface ModeCardProps {
  value: ProjectDestinationMode;
  checked: boolean;
  onSelect: (mode: ProjectDestinationMode) => void;
  icon: LucideIcon;
  title: string;
  description: string;
  disabled: boolean;
}

/**
 * One option of the move/copy choice. A native radio inside a label, so the
 * pair gets arrow-key navigation and radio semantics without a custom widget;
 * the card around it is only the visible target.
 */
function ModeCard({
  value,
  checked,
  onSelect,
  icon: Icon,
  title,
  description,
  disabled,
}: ModeCardProps) {
  return (
    <label
      className={cn(
        "relative flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors",
        "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2",
        checked ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50",
        disabled && "cursor-not-allowed opacity-60"
      )}
    >
      <input
        type="radio"
        name="project-destination-mode"
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="sr-only"
      />
      <span className="flex items-center gap-2 text-sm font-medium">
        <Icon className={cn("h-4 w-4", checked ? "text-primary" : "text-muted-foreground")} />
        {title}
        {checked && <Check className="ml-auto h-4 w-4 text-primary" aria-hidden />}
      </span>
      <span className="text-xs leading-snug text-muted-foreground">{description}</span>
    </label>
  );
}

export const ProjectDestinationModal = observer(function ProjectDestinationModal({
  isOpen,
  onClose,
  onConfirm,
  projects,
  currentProjectId,
  buildingId,
  itemCount = 1,
  isLoading = false,
}: ProjectDestinationModalProps) {
  const { t } = useLanguage();
  const [selectedProjectId, setSelectedProjectId] = useState<string>(currentProjectId);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [folders, setFolders] = useState<TreeFolder[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderLoadError, setFolderLoadError] = useState(false);
  const [folderLoadAttempt, setFolderLoadAttempt] = useState(0);
  const [mode, setMode] = useState<ProjectDestinationMode>("move");
  const isCopy = mode === "copy";

  // Other projects for "Change project" dropdown, narrowed by the status
  // filter (the chosen destination is always shown).
  const otherProjects = projects.filter((p) => p.id !== currentProjectId);
  const statusFilter = useProjectStatusFilter(otherProjects, selectedProjectId);

  // Load folders when modal opens or project changes. `isOpen` is in the deps so
  // reopening the modal refetches — prevents stale lists when a folder was created
  // while the modal was closed (WHA-2994).
  useEffect(() => {
    let cancelled = false;

    async function loadFolders() {
      if (!isOpen || !selectedProjectId) {
        setFolders([]);
        setFolderLoadError(false);
        setLoadingFolders(false);
        return;
      }

      setFolders([]);
      setSelectedFolderId(null);
      setFolderLoadError(false);
      setLoadingFolders(true);
      try {
        const projectFolders = await folderService.getProjectFolders(buildingId, selectedProjectId);
        const tree = folderService.buildEnhancedFolderTree(projectFolders);
        if (!cancelled) setFolders(tree);
      } catch (error) {
        if (!cancelled) {
          void logger.error(
            "ui",
            "project-destination-folders-load-failed",
            "Failed to load destination folders",
            error instanceof Error ? error : new Error(String(error)),
            { buildingId, projectId: selectedProjectId }
          );
          setFolderLoadError(true);
          setFolders([]);
        }
      } finally {
        if (!cancelled) setLoadingFolders(false);
      }
    }

    void loadFolders();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, buildingId, isOpen, folderLoadAttempt]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedProjectId(currentProjectId);
      setSelectedFolderId(null);
      setExpandedFolders(new Set());
      setMode("move");
    }
  }, [isOpen, currentProjectId]);

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const renderFolderTree = (folders: TreeFolder[], level = 0) => {
    return folders.map((folder) => (
      <div key={folder.id} style={{ marginLeft: `${level * 16}px` }}>
        <div
          className={`flex cursor-pointer items-center gap-2 rounded p-2 ${
            selectedFolderId === folder.id
              ? "bg-primary/10 text-primary ring-1 ring-primary/30"
              : "hover:bg-muted/50"
          }`}
          onClick={() => setSelectedFolderId(selectedFolderId === folder.id ? null : folder.id)}
        >
          {(folder.children?.length ?? 0) > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="rounded p-1 hover:bg-muted"
            >
              {expandedFolders.has(folder.id) ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
            </button>
          )}
          {!folder.children?.length && <div className="w-5" />}

          <FolderIcon className="h-4 w-4 text-primary" />
          <span className="text-sm">{folder.name}</span>
          {selectedFolderId === folder.id && <Check className="ml-auto h-4 w-4 text-primary" />}
        </div>

        {expandedFolders.has(folder.id) && (folder.children?.length ?? 0) > 0 && (
          <div>{renderFolderTree(folder.children ?? [], level + 1)}</div>
        )}
      </div>
    ));
  };

  const handleConfirm = () => {
    onConfirm({
      projectId: selectedProjectId,
      folderId: selectedFolderId,
      mode,
    });
  };

  // Can confirm once folders have finished loading AND a destination
  // folder has been selected. Without the selection check the Move button
  // would fire a no-op move to the project root and show a misleading
  // success toast. (WHA-3261)
  const canConfirm = !loadingFolders && !folderLoadError && selectedFolderId !== null;
  const copy = t.projects.destination;

  const currentProject = projects.find((p) => p.id === currentProjectId);
  const currentProjectName = currentProject?.name ?? copy.currentProjectFallback;
  const selectedProjectName =
    selectedProjectId === currentProjectId
      ? currentProjectName
      : projects.find((p) => p.id === selectedProjectId)?.name;
  const selectedPath = selectedFolderId !== null ? findFolderPath(folders, selectedFolderId) : null;

  const title = (
    isCopy
      ? itemCount === 1
        ? copy.titleCopyOne
        : copy.titleCopyMany
      : itemCount === 1
        ? copy.titleMoveOne
        : copy.titleMoveMany
  ).replace("{count}", String(itemCount));
  const TitleIcon = isCopy ? Copy : FolderInput;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Capped to the viewport: the folder tree is the one part that gives way, so
          the header and the confirm button stay on screen on short windows. */}
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TitleIcon className="h-5 w-5" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="-m-1 flex min-h-0 min-w-0 flex-1 flex-col gap-5 overflow-y-auto p-1">
          {/* Move vs copy — the decision with consequences, so it comes first
              and each option says what happens to the originals. */}
          <fieldset className="space-y-2">
            <legend className="mb-2 text-sm font-medium">{copy.modeLabel}</legend>
            <div className="grid grid-cols-2 gap-3">
              <ModeCard
                value="move"
                checked={!isCopy}
                onSelect={setMode}
                icon={FolderInput}
                title={copy.moveTitle}
                description={copy.moveDescription}
                disabled={isLoading}
              />
              <ModeCard
                value="copy"
                checked={isCopy}
                onSelect={setMode}
                icon={Copy}
                title={copy.copyTitle}
                description={copy.copyDescription}
                disabled={isLoading}
              />
            </div>
          </fieldset>

          <section className="flex min-h-0 flex-1 flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {copy.destinationHeading}
            </h3>

            {/* Project selector - optional, defaults to current */}
            <div className="space-y-1.5">
              <Label htmlFor="project-select">{copy.projectLabel}</Label>
              <Select
                value={selectedProjectId}
                onValueChange={(value) => {
                  // A folder belongs to exactly one project. Switching projects
                  // must drop the stale folder selection — otherwise Move writes a
                  // folder from the old project against the new project_id and
                  // orphans the doc (counted under one project, listed under none).
                  setSelectedProjectId(value);
                  setSelectedFolderId(null);
                  setExpandedFolders(new Set());
                }}
              >
                <SelectTrigger id="project-select">
                  <SelectValue>
                    {selectedProjectId === currentProjectId
                      ? `${currentProjectName} ${copy.current}`
                      : selectedProjectName}
                  </SelectValue>
                </SelectTrigger>
                <ProjectSelectContent statusFilter={statusFilter}>
                  <SelectItem value={currentProjectId}>
                    <span className="inline-flex items-center gap-1.5">
                      {currentProject && (
                        <ProjectStatusBadge variant="dot" status={currentProject.status} />
                      )}
                      {currentProjectName} {copy.current}
                    </span>
                  </SelectItem>
                  {/* Every project stays mounted — see isShown. */}
                  {statusFilter.ordered.map((project) => (
                    <SelectItem
                      key={project.id}
                      value={project.id}
                      disabled={!statusFilter.isShown(project)}
                      className={cn(!statusFilter.isShown(project) && "hidden")}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <ProjectStatusBadge variant="dot" status={project.status} />
                        {project.name}
                      </span>
                    </SelectItem>
                  ))}
                </ProjectSelectContent>
              </Select>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-1.5">
              <Label>{copy.folderLabel}</Label>
              <div className="h-64 min-h-32 shrink overflow-y-auto rounded-md border p-2">
                {loadingFolders ? (
                  <div className="space-y-2 p-1" role="status" aria-label={copy.loadingFolders}>
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-7" style={{ width: `${85 - i * 12}%` }} />
                    ))}
                  </div>
                ) : folderLoadError ? (
                  <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-sm">
                    <p role="alert">{copy.folderLoadError}</p>
                    <Button
                      variant="outline"
                      onClick={() => setFolderLoadAttempt((attempt) => attempt + 1)}
                    >
                      {t.common.retry}
                    </Button>
                  </div>
                ) : folders.length > 0 ? (
                  renderFolderTree(folders)
                ) : (
                  <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    {copy.noFolders}
                  </p>
                )}
              </div>
            </div>

            {/* Where the items will end up, spelled out, so the choice above and
                the folder picked here read as one sentence before confirming. */}
            <div className="flex min-w-0 items-center gap-2 text-sm" aria-live="polite">
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              {selectedPath ? (
                <span className="min-w-0 truncate">
                  <span className="text-muted-foreground">
                    {isCopy ? copy.summaryCopy : copy.summaryMove}{" "}
                  </span>
                  <span className="font-medium">
                    {[selectedProjectName, ...selectedPath.map((f) => f.name)]
                      .filter(Boolean)
                      .join(" \u203A ")}
                  </span>
                </span>
              ) : (
                <span className="text-muted-foreground">{copy.selectFolderHint}</span>
              )}
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {t.common.cancel}
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || isLoading} className="gap-2">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isCopy ? copy.copying : copy.moving}
              </>
            ) : (
              <>
                <TitleIcon className="h-4 w-4" />
                {isCopy ? copy.confirmCopy : copy.confirmMove}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
